/**
 * Thin wrappers around the speech-recognition commands exposed by
 * tauri-plugin-voice.
 *
 * Not a hook in the React sense — just a trio of async helpers that
 * mirror the Rust API. Kept in hooks/ because it lives alongside
 * useVoiceRecording and callers will reach for both from the same
 * conceptual slot.
 *
 * On desktop and web these resolve to stubbed values so features that
 * call them compile and render.
 */
import { invoke } from "@tauri-apps/api/core";

import type {
  SpeechPermissionState,
  SpeechPermissionStatus,
  VoiceTranscription,
} from "@/types/voice";

const isNative =
  typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;

export async function checkSpeechPermission(): Promise<SpeechPermissionState> {
  if (!isNative) return "granted";
  const result = await invoke<SpeechPermissionStatus>(
    "plugin:voice|check_speech_permissions"
  );
  return result.status;
}

export async function requestSpeechPermission(): Promise<SpeechPermissionState> {
  if (!isNative) return "granted";
  const result = await invoke<SpeechPermissionStatus>(
    "plugin:voice|request_speech_permissions"
  );
  return result.status;
}

/**
 * Transcribe an audio file at `path` on-device. Returns empty text
 * on desktop/web so callers can render the same flow everywhere.
 * Throws on iOS if the file is missing, the recognizer is unavailable,
 * or permission is denied — callers should try-catch and fall back to
 * saving the answer without a transcript.
 */
export async function transcribeAudio(path: string): Promise<VoiceTranscription> {
  if (!isNative) return { text: "", locale: "en-US" };
  return invoke<VoiceTranscription>("plugin:voice|transcribe_audio", { path });
}
